import requester from '@zg-rentals/particles-js-requester';
import type { ApiRequestInterface } from '@zg-rentals/web-base';
import type { ApiResponseDto } from './ApiResponseDto';
import type { RentalManagerEnvironment } from '@zg-rentals/rental-manager-env-base';
import { ContextError, logError } from '@zg-rentals/log-error';
import { DatarouterError, finalizeBodyData, isLocalhost } from '@zg-rentals/web-base';
import { RentalManagerBrowserEnvironment } from '@zg-rentals/rental-manager-env-base';
import { createRequestHeaders } from '@zg-rentals/particles-js-requester';

interface RentalManagerApiRequestInterface<G, P> extends ApiRequestInterface<G, P> {
  environment?: RentalManagerEnvironment;
}

// Makes a front end response, all header and actual http logic is abstracted away
export const frontEndRequestV2 = async <G, P, R>({
  getParams,
  postData,
  endpoint,
  method,
  environment,
  isFormData,
  contextPath,
}: RentalManagerApiRequestInterface<G, P>): Promise<R> => {
  try {
    if (typeof window === 'undefined') {
      if (!environment) {
        throw new ContextError('frontEndRequestV2.RentalManagerEnvironmentMissing', { contextPath, endpoint, method });
      }
    }
    if (environment === undefined) {
      environment = RentalManagerBrowserEnvironment.getInstance();
    }

    const finalBodyData = finalizeBodyData(isFormData || false, postData);

    const app = environment.nodeEnv;
    const requestConfig = {
      params: getParams,
      headers: createRequestHeaders({
        brand: app.brand,
        ip: app.ip,
        userAgent: app.userAgent,
        isNode: environment.isServer,
        buildId: app.buildId,
        method: method,
        // only neccesary for ssr
        cookies: environment.isServer ? app.originalServerSideRequestCookies : undefined,
        headers: {
          'Content-Type': isFormData ? 'multipart/form-data' : '',
        },
      }),
    };

    let targetApi: string;
    if (environment.isServer) {
      // On the server, always target `zgApi` directly.
      targetApi = environment.nodeEnv.zgApi;
    } else if (environment.proxyPath) {
      // If an app has a proxy path configured, it's obviously a proxy running on the same origin/host
      targetApi = window.location.origin;
    } else {
      // For localhost origins, use `zgApi` directly (CORS is allowed for localhost), otherwise use `origin` because
      // the app is _most likely_ served up by the same server hosting the API.
      targetApi = isLocalhost(window.location.host) ? environment.nodeEnv.zgApi || '' : window.location.origin;
    }

    // Note: proxyPath handling in `requester` attempts to be SSR-aware, but there is separate SSR-specific
    // logic already, so bypass `requester` logic by omitting `proxyPath` option.
    const client = requester({
      api: targetApi,
      logger: environment.logger,
    });

    try {
      // Include proxyPath if configured, then join contextPath and endpoint taking care to collapse multple
      // slashes (inconcistent configuration sources can cause this)
      const path = [environment.proxyPath, contextPath, endpoint]
        .filter((part) => part != null)
        .join('/')
        .replace(/\/+/g, '/');

      let httpResponse;
      if (method === 'GET') {
        httpResponse = await client.get(path, requestConfig);
      } else if (method === 'POST') {
        httpResponse = await client.post(path, finalBodyData, requestConfig);
      } else {
        throw new ContextError('frontEndRequestV2.UnsupportedMethod', { contextPath, endpoint, method });
      }

      const rentalPlatformResult: ApiResponseDto<R> = httpResponse.data;

      if (!rentalPlatformResult.success) {
        if (rentalPlatformResult.errors) {
          throw new DatarouterError('READ: rentalPlatformResult.errors', '', httpResponse.status, rentalPlatformResult);
        }

        throw new DatarouterError(
          rentalPlatformResult.error.message,
          rentalPlatformResult.error.code,
          httpResponse.status,
          rentalPlatformResult.error,
        );
      }
      if (!environment.isClient) {
        // Aggregate cookies from back end calls and pass onto front end
        if (httpResponse.headers['set-cookie']) {
          app.apiResponseServerSideRequestCookies = app.apiResponseServerSideRequestCookies.concat(
            httpResponse.headers['set-cookie'],
          );
        }
      }
      return rentalPlatformResult.response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      delete err.config;
      delete err.request;

      err.errorType = 'rental-manager-env-api';
      err.endpoint = endpoint;
      err.body = finalBodyData;
      err.params = getParams;

      if (err?.error?.stack) {
        err.error.stack = err.error.stack.substring(0, 10000);
      }

      if (DatarouterError.is(err)) {
        // already app layer - skip formatting
        throw err;
      }

      if (err?.response?.data?.error?.message) {
        throw new DatarouterError(
          err.response.data.error.message,
          err.response.data.error.code,
          err.response.status,
          err.response.data,
        );
      }

      if (err?.response?.data?.errors?.length || err?.response?.data?.error?.data?.errorByFieldName) {
        throw new DatarouterError('READ: err?.errorDetails', '', err.response.status, err.response.data);
      }

      // Note: throwing err only fails with strict mode enabled, so we need to ts-ignore
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      throw new Error(err);
    }
  } catch (error) {
    logError({
      error,
      context: {
        method,
        endpoint,
        contextPath,
      },
      errorType: 'HTTPError',
    });

    throw error;
  }
};
